import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataTableSkeleton,
  DataType,
  FormDialogMode,
  SelectOption,
  useNotify,
  UserPermission
} from '@globaleris/ui.library'
import { Box, makeStyles, useTheme } from '@material-ui/core'
import { Delete, Edit } from '@material-ui/icons'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AnnexedParamsModel } from '../../../api/Argentia/endpoints/annexedParams/model/annexedParams'
import { EnumTipoValor } from '../../../api/oData/enums/enumTipoValor'
import { enumToSelectOptionArray } from '../../../helpers'
import { missingFieldsFormWithRequiredText } from '../../../helpers/validateFormHelper'
import { FormDialogDefinitionProps, useFormDialog } from '../../../hooks'
import { getCustomerDomain } from '../../../redux'
import { AccordionSection } from '../accordionSection'
import {
  mapAnnexedParamsToSelectOption,
  mapAnnexedParamValuesToSelectOption,
  mapParamDescriptionsToTableData,
  mapSelectedRowToAnnexedParamForm
} from './annexedParamAccordionSection.mapper'
import {
  AnnexedParamSectionForm,
  AnnexedParamSectionTableModel
} from './annexedParamAccordionSection.model'
import { styles } from './annexedParamAccordionSection.styles'

interface AnnexedParamAccordionProps {
  title: string
  isLoading?: boolean
  withDescriptions?: boolean
  tableData: AnnexedParamSectionTableModel[]
  annexedParamsOptions: AnnexedParamsModel[]
  onRowAdd: (formData: AnnexedParamSectionForm) => void
  onRowEdit: (formData: AnnexedParamSectionForm) => void
  onRowDelete: (selectedRow: AnnexedParamSectionTableModel) => void
  defaultExpanded?: boolean
}

export const AnnexedParamAccordionSection: React.FC<AnnexedParamAccordionProps> = ({
  title,
  isLoading = false,
  withDescriptions = false,
  tableData,
  annexedParamsOptions,
  onRowAdd,
  onRowEdit,
  onRowDelete,
  defaultExpanded = false
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'annexedParamAccordionSection', 'enums'])
  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()
  const { openForm, updateFormData, updateFormDefinition, closeForm, formProps, CustomFormDialog } =
    useFormDialog()
  const { error } = useNotify()

  const [formMode, setFormMode] = useState<FormDialogMode | null>(null) //state for modal Form Management
  const [inputDataType, setInputDataType] = useState<number>()
  const [annexedParamsListOptions, setAnnexedParamsListOptions] = useState<SelectOption[]>([])

  const annexedParamSelectOption = mapAnnexedParamsToSelectOption(annexedParamsOptions)
  const tableDataWithParamDescriptions = mapParamDescriptionsToTableData(
    tableData,
    annexedParamsOptions
  )
  // #region Handlers Starts
  const handleInsertForm = async () => {
    setFormMode(FormDialogMode.ADD)
    openForm({
      definition: annexedParamFormDefinition,
      title: t('annexedParamAccordionSection:forms.title'),
      mode: FormDialogMode.ADD,
      isWaiting: false,
      onClose: formData => {
        handleFormClose(formData, onRowAdd)
      }
    })
    return true
  }

  const handleEditForm = (selectedRow: AnnexedParamSectionTableModel) => {
    const selectedParamInfo = annexedParamsOptions.find(
      param => param.id === selectedRow.annexedParam
    )

    let inputData: AnnexedParamSectionForm = mapSelectedRowToAnnexedParamForm(selectedRow)

    if (selectedRow.valueType === EnumTipoValor.List) {
      const code = selectedRow.value.split(' - ')[0]

      inputData = {
        ...inputData,
        value_list: selectedParamInfo?.annexedParamsValues?.find(pv => pv.code === code)?.id
      }

      setAnnexedParamsListOptions(
        mapAnnexedParamValuesToSelectOption(selectedParamInfo?.annexedParamsValues ?? [])
      )
    }

    setFormMode(FormDialogMode.EDIT)
    openForm({
      definition: annexedParamFormDefinition,
      title: t('annexedParamAccordionSection:forms.editTitle'),
      mode: FormDialogMode.EDIT,
      inputData: inputData,
      isWaiting: false,
      onClose: formData => {
        handleFormClose(formData, onRowEdit)
      }
    })
    setInputDataType(selectedRow.valueType)
  }

  const handleFormClose = async (
    formData: AnnexedParamSectionForm,
    callback: (formData: AnnexedParamSectionForm) => void
  ) => {
    let formIsIncomplete: boolean = false
    if (formData) {
      if (
        !missingFieldsFormWithRequiredText(
          annexedParamFormDefinitionRef.current.sections!,
          formData
        )
      ) {
        callback(formData)
      } else {
        error(t('annexedParamAccordionSection:forms.notifications.incompleteForm'))
        formIsIncomplete = true
      }
    }
    if (!formIsIncomplete) {
      closeForm()
      setFormMode(null)
      setInputDataType(undefined)
    }
  }

  const handleDelete = (selectedRow: AnnexedParamSectionTableModel) => {
    onRowDelete(selectedRow)
  }

  // #endregion Handlers Ends

  // #region Functions Starts
  const valueTypeIsEqual = useCallback(
    (inputValue: number | undefined, expectedValue: EnumTipoValor) => {
      return inputValue ? inputValue === expectedValue : false
    },
    []
  )

  const filterUsedParams = useCallback(
    (paramsListArray: SelectOption[]) => {
      const filteredParams = paramsListArray.filter(
        param => !tableData?.some(row => row.annexedParam === param.value)
      )
      return filteredParams
    },
    [tableData]
  )
  const getAnnexedParamValueId = (
    rowParamId: number,
    paramLabel: string,
    paramsList: AnnexedParamsModel[]
  ) => {
    const paramValues = paramsList.find(param => param.id === rowParamId)?.annexedParamsValues
    const id = paramValues?.find(paramValue => paramValue.description === paramLabel)?.id
    return id
  }
  // #endregion Functions Ends

  // #region Form Definition Starts
  const annexedParamFormDefinition = React.useMemo<FormDialogDefinitionProps>(
    (): FormDialogDefinitionProps => ({
      id: 'ADD_ANNEXED_PARAMS',
      sections: [
        {
          inputs: [
            {
              name: 'description',
              type: DataType.TEXT,
              display: false
            },
            {
              name: 'detailedDescription',
              type: DataType.TEXT,
              display: false
            },
            {
              name: 'annexedParamId',
              label: t('annexedParamAccordionSection:forms.inputs.annexedParamId'),
              type: DataType.SELECT,
              selectOptions:
                formMode === FormDialogMode.ADD
                  ? filterUsedParams(annexedParamSelectOption)
                  : annexedParamSelectOption,
              inputSize: 4,
              readOnly: formMode === FormDialogMode.EDIT ? true : false,
              required: true,
              onChange: (value, formState) => {
                const annexedParam = annexedParamsOptions.find(param => param.id === value)
                updateFormData(prevState => ({
                  description: annexedParam?.description,
                  detailedDescription: annexedParam?.detailedDescription,
                  annexedParamId: +value,
                  valueTypeId: annexedParam?.valueTypeId,
                  value_bool: annexedParam?.valueTypeId === EnumTipoValor.Bool ? false : undefined // bool can't handle defaultValue so this step is required
                }))

                setInputDataType(annexedParam?.valueTypeId)

                if (annexedParam?.valueTypeId === EnumTipoValor.List && annexedParam) {
                  setAnnexedParamsListOptions(
                    mapAnnexedParamValuesToSelectOption(annexedParam?.annexedParamsValues ?? [])
                  )
                }
              }
            },
            {
              name: 'valueTypeId',
              label: t('annexedParamAccordionSection:forms.inputs.valueTypeId'),
              type: DataType.SELECT,
              selectOptions: enumToSelectOptionArray(EnumTipoValor, 'enums:EnumTipoValor'),
              readOnly: true,
              inputSize: 4
            },
            {
              name: 'value_text',
              label: t('annexedParamAccordionSection:forms.inputs.value'),
              type: DataType.TEXT,
              defaultValue: null,
              maxLength: inputDataType === EnumTipoValor.Char ? 1 : 100,
              inputSize: 4,
              helpText:
                inputDataType === EnumTipoValor.Char
                  ? t('annexedParamAccordionSection:forms.helpText.char')
                  : t('annexedParamAccordionSection:forms.helpText.string'),
              display:
                valueTypeIsEqual(inputDataType, EnumTipoValor.Char) ||
                valueTypeIsEqual(inputDataType, EnumTipoValor.String),
              required:
                valueTypeIsEqual(inputDataType, EnumTipoValor.Char) ||
                valueTypeIsEqual(inputDataType, EnumTipoValor.String)
            },
            {
              name: 'value_number',
              label: t('annexedParamAccordionSection:forms.inputs.value'),
              type: DataType.NUMBER,
              customDecimalScale: inputDataType === EnumTipoValor.Int ? 0 : 6,
              inputSize: 4,
              helpText:
                inputDataType === EnumTipoValor.Int
                  ? t('annexedParamAccordionSection:forms.helpText.int')
                  : undefined,
              display:
                valueTypeIsEqual(inputDataType, EnumTipoValor.Int) ||
                valueTypeIsEqual(inputDataType, EnumTipoValor.Decimal),
              required:
                valueTypeIsEqual(inputDataType, EnumTipoValor.Int) ||
                valueTypeIsEqual(inputDataType, EnumTipoValor.Decimal)
            },
            {
              name: 'value_date',
              label: t('annexedParamAccordionSection:forms.inputs.value'),
              type: DataType.DATE,
              inputSize: 4,
              display: valueTypeIsEqual(inputDataType, EnumTipoValor.DateTime),
              required: valueTypeIsEqual(inputDataType, EnumTipoValor.DateTime)
            },
            {
              name: 'value_bool',
              label: t('annexedParamAccordionSection:forms.inputs.valueSwitch'),
              defaultValue: 0,
              type: DataType.SWITCH,
              inputSize: 4,
              display: valueTypeIsEqual(inputDataType, EnumTipoValor.Bool),
              required: valueTypeIsEqual(inputDataType, EnumTipoValor.Bool)
            },
            {
              name: 'value_list',
              label: t('annexedParamAccordionSection:forms.inputs.value'),
              type: DataType.SELECT,
              inputSize: 4,
              selectOptions: annexedParamsListOptions,
              defaultValue: annexedParamsListOptions[0]?.value,
              display: valueTypeIsEqual(inputDataType, EnumTipoValor.List),
              required: valueTypeIsEqual(inputDataType, EnumTipoValor.List),
              onChange: (value, formState) => {
                updateFormData(prevState => ({
                  ...prevState,
                  value_list_label: annexedParamsListOptions.find(param => param.value === +value)
                    ?.label
                }))
              }
            }
          ]
        }
      ]
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }),
    [
      // annexedParamSelectOption,
      // annexedParamsOptions,
      valueTypeIsEqual,
      filterUsedParams,
      inputDataType,
      annexedParamsListOptions,
      formMode
    ]
  )

  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  const annexedParamsTableOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: true,
    elevation: 0,
    download: false,
    canInsert: true,
    customInsertEditModal: true,
    onRowInsert: handleInsertForm,
    rowCustomActions: [
      {
        userPermission: UserPermission.EDIT,
        icon: <Edit fontSize="small" />,
        tooltip: t('annexedParamAccordionSection:dataTable.tooltips.edit'),
        onClick: handleEditForm
      },
      {
        userPermission: UserPermission.EDIT,
        icon: <Delete fontSize="small" />,
        tooltip: t('annexedParamAccordionSection:dataTable.tooltips.delete'),
        onClick: handleDelete
      }
    ],
    analytics: {
      customer: getCustomerDomain(),
      view: t('annexedParamAccordionSection:dataTable.analytics')
    }
  }

  const annexedParamsColumns: DataTableColumn[] = [
    {
      name: 'entityId',
      display: false
    },
    {
      name: 'annexedEntityId',
      display: false
    },
    {
      name: 'description',
      label: t('annexedParamAccordionSection:dataTable.columns.description'),
      type: DataType.TEXT,
      display: true
    },
    {
      name: 'detailedDescription',
      label: t('annexedParamAccordionSection:dataTable.columns.detailedDescription'),
      type: DataType.TEXT,
      display: true
    },
    {
      name: 'valueType',
      label: t('annexedParamAccordionSection:dataTable.columns.valueType'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(EnumTipoValor, 'enums:EnumTipoValor')
    },
    {
      name: 'value',
      label: t('annexedParamAccordionSection:dataTable.columns.value'),
      type: DataType.TEXT
    }
  ]
  // #endregion DataTable Config Ends

  // #region Hooks Starts
  const annexedParamFormDefinitionRef = useRef(annexedParamFormDefinition)
  useEffect(() => {
    annexedParamFormDefinitionRef.current = annexedParamFormDefinition
  }, [annexedParamFormDefinition])

  useEffect(() => {
    updateFormDefinition(annexedParamFormDefinition)
  }, [annexedParamFormDefinition])
  // #endregion Hooks Ends

  return (
    <AccordionSection
      defaultExpanded={defaultExpanded}
      sectionTitle={title}
      style={{
        margin: '0px 16px 16px 16px',
        padding: '0px 0px 16px 0px'
      }}
    >
      <Box style={{ width: '100%', overflow: 'auto' }}>
        {isLoading ? (
          <DataTableSkeleton rows={3} elevation={0} />
        ) : (
          <DataTable
            className={classes.annexedContractsDataTable}
            data={tableDataWithParamDescriptions}
            columns={annexedParamsColumns}
            options={annexedParamsTableOptions}
          />
        )}
      </Box>
      <CustomFormDialog {...formProps} />
    </AccordionSection>
  )
}
